import { getUserConfig, getUserInfo, updateUserConfig, UserConfig } from "../services/userService";
import type { SettingScope } from "./types";

export interface LayerAdapter {
  load(): Promise<Record<string, unknown>>;
  /** 여러 키를 한 번에 저장. 값이 null이면 삭제 */
  saveMany(patch: Record<string, unknown>): Promise<void>;
}

const DEVICE_KEY = "app.settings.device";
const DEVICE_VERSION = 1;

class DeviceAdapter implements LayerAdapter {
  private cache: Record<string, unknown> = {};

  async load() {
    try {
      const raw = localStorage.getItem(DEVICE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (parsed?._v !== DEVICE_VERSION) {
        localStorage.removeItem(DEVICE_KEY);
        return {};
      }
      this.cache = parsed.values ?? {};
      return this.cache;
    } catch {
      localStorage.removeItem(DEVICE_KEY);
      return {};
    }
  }

  async saveMany(patch: Record<string, unknown>) {
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null) delete this.cache[k];
      else this.cache[k] = v;
    });
    localStorage.setItem(DEVICE_KEY, JSON.stringify({ _v: DEVICE_VERSION, values: this.cache }));
  }
}

function readUserId(): number | null {
  try {
    const raw = localStorage.getItem("userInfo");
    if (!raw) return null;
    const info = JSON.parse(raw);
    const id = Number(info?.id);
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
}

class UserAdapter implements LayerAdapter {
  private full: UserConfig = { keyMap: [] };

  async load() {
    const userId = readUserId();
    if (userId == null) return {};

    const res = await getUserInfo(userId); // 응답: { user: { config } }
    this.full = res?.user?.config ?? { keyMap: [] };
    return this.full.settings ?? {};
  }

  async saveMany(patch: Record<string, unknown>) {
    const userId = readUserId();
    if (userId == null) throw new Error("로그인이 필요합니다");

    // PUT은 전체 덮어쓰기이므로 항상 최신 상태 위에 얹는다
    const res = await getUserInfo(userId);
    const base: UserConfig = res?.user?.config ?? { keyMap: [] };

    const settings = { ...(base.settings ?? {}) };
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null) delete settings[k];
      else settings[k] = v;
    });

    const next: UserConfig = { ...base, settings };

    const result = await updateUserConfig(next, userId);
    if (result === undefined) throw new Error("설정 저장에 실패했습니다");

    this.full = next;
  }
}

/** site 레이어 — config.json에서 읽기만 함 */
class ReadOnlyAdapter implements LayerAdapter {
  async load() {
    return {};
  }
  async saveMany(): Promise<never> {
    throw new Error("수정할 수 없는 레이어입니다");
  }
}

export const userAdapter = new UserAdapter();

export const adapters: Record<SettingScope, LayerAdapter> = {
  site: new ReadOnlyAdapter(),
  user: userAdapter,
  device: new DeviceAdapter(),
};

export async function saveUserSettings(settings: Record<string, unknown>) {
  const userId = readUserId();
  if (userId == null) throw new Error("로그인이 필요합니다");

  const res = await getUserInfo(userId);
  const cfg = res?.user?.config ?? {};
  const inner = cfg?.user?.config ?? cfg;

  const next = { ...inner, settings };

  const result = await updateUserConfig(next as UserConfig, userId);
  if (result === undefined) throw new Error("설정 저장에 실패했습니다");
}

export function saveDeviceSettings(values: Record<string, unknown>) {
  localStorage.setItem(DEVICE_KEY, JSON.stringify({ _v: DEVICE_VERSION, values }));
}
